import { z } from "zod";
import { publicPageUrl } from "./privacy";

/**
 * 上架事实包（launch fact pack）。
 *
 * 这是「选品到上架」链路里**唯一**允许 Listing 与填写计划消费的商品事实来源。
 * 它刻意与选品初筛使用的 `sourceFacts` 分开：
 *
 * - `sourceFacts` 是前三部分的历史输入，可以被污染（站点后缀标题、
 *   把推荐位价格当采购价、把整个规格区压成一个字符串）；
 * - 上架事实包只保存**可追溯到一次具体观察**的字段，并逐项带来源与确认状态。
 *
 * 判定「能不能进 Listing」的规则集中在本文件里，而不是分散到 API 与插件两处：
 * 两边各写一遍正则，迟早会漂移出「一边说干净、一边说污染」的静默分歧。
 *
 * 事实包里**只存原始观察**，阶梯价、起批量、待核实列表都由事实派生
 * （见 `resolvedTierPrices` / `resolvedMinimumOrderQuantity` / `deriveMissingLaunchFacts`），
 * 不另存第二份，避免两份数据各自过期。
 */

/** 一项上架事实是怎么被知道的。页面观察与截图识别都不等于用户确认。 */
export const LaunchFactSourceSchema = z.enum(["page_observed", "screenshot_observed", "user_confirmed", "needs_review"]);
export type LaunchFactSource = z.infer<typeof LaunchFactSourceSchema>;

/** 允许进入 Listing 的来源。`needs_review` 永远不在其中。 */
export const LISTING_TRUSTED_LAUNCH_FACT_SOURCES = ["page_observed", "screenshot_observed", "user_confirmed"] as const;
export const isListingTrustedLaunchFactSource = (source: LaunchFactSource): boolean =>
  (LISTING_TRUSTED_LAUNCH_FACT_SOURCES as readonly string[]).includes(source);

export const LaunchFactKeySchema = z.enum([
  "product_title", "tier_prices", "minimum_order_quantity", "material",
  "dimensions", "weight", "packaging", "brand", "certification", "compliance",
]);
export type LaunchFactKey = z.infer<typeof LaunchFactKeySchema>;

export const LAUNCH_FACT_LABELS: Record<LaunchFactKey, string> = {
  product_title: "商品标题",
  tier_prices: "阶梯采购价",
  minimum_order_quantity: "起批量",
  material: "材质",
  dimensions: "尺寸",
  weight: "重量",
  packaging: "包装",
  brand: "品牌",
  certification: "认证",
  compliance: "合规信息",
};

/** 没有这一项就没法生成 Listing。 */
export const LAUNCH_FACT_REQUIRED_FOR_LISTING: readonly LaunchFactKey[] = ["product_title"];

/** 这些项缺失时进「待核实列表」，不允许被写成商品卖点。 */
export const LAUNCH_FACT_VERIFY_KEYS: readonly LaunchFactKey[] = [
  "material", "dimensions", "weight", "packaging", "brand", "certification", "compliance",
];

/**
 * 高风险事实：写进 Listing 前必须由用户确认。
 * 猜错一项，后果是整条 Listing 重做或直接违规。
 */
export const LAUNCH_FACT_HIGH_RISK_KEYS: readonly LaunchFactKey[] = ["material", "brand", "certification", "compliance"];

/** 采购成本不是上架文案，任何情况下都不进 Listing。 */
export const LAUNCH_FACT_COPY_EXCLUDED_KEYS: readonly LaunchFactKey[] = ["tier_prices"];

/**
 * 页面导航／模块名称。出现在抽取值里说明这段文字根本不是这个字段的值。
 *
 * 前六项来自设计文档；其余是同一类模块名，加进来必须同步补测试——
 * 这是一份「宁可少收，不可错杀」的清单：漏判只是多一次人工确认，
 * 误判会把正确的材质当成污染的丢掉。
 */
export const LAUNCH_NAVIGATION_TERMS: readonly string[] = [
  "热门推荐", "同款推荐", "商品评价", "官方验货", "商品详情", "包装信息",
  "相关推荐", "猜你喜欢", "看了又看", "为你推荐", "店铺推荐", "供应商推荐",
];

/** 超过这个长度说明抽取值吞进了相邻内容。 */
export const LAUNCH_FACT_LENGTH_LIMITS: Partial<Record<LaunchFactKey, number>> = {
  product_title: 300,
  material: 60,
  dimensions: 80,
  weight: 40,
  packaging: 80,
  brand: 80,
  certification: 120,
  compliance: 200,
};

/** 起批量只接受这些单位；"面议""大量"一律不可解析。 */
export const LAUNCH_QUANTITY_UNITS: readonly string[] = [
  "件", "个", "只", "条", "套", "箱", "包", "盒", "双", "张", "对", "公斤", "千克", "kg", "米",
];
const UNIT = LAUNCH_QUANTITY_UNITS.join("|");

export const LaunchFactSchema = z.object({
  key: LaunchFactKeySchema,
  label: z.string().min(1).max(80),
  /** 规范化后的值：真正会被展示、被写进 Listing 的那一份。 */
  value: z.string().min(1).max(600),
  /** 页面原文，便于用户核对抽取值对不对。 */
  raw: z.string().max(600).optional(),
  source: LaunchFactSourceSchema,
  observedAt: z.string(),
  pageUrl: z.string().max(1200).optional(),
  /**
   * 这一项是从哪种观察面读到的。截图兜底新增；省略即页面 DOM 字段。
   * 留档是为了让用户看得出来「这条是整页截图认出来的」，而不是页面结构读的。
   */
  region: z.enum(["whole_page", "field"]).optional(),
  /** 字段标签与值之间是否有可证明的页面邻接关系。 */
  labelAdjacency: z.boolean(),
  warnings: z.array(z.string().min(1).max(240)).max(6).default([]),
}).strict();
export type LaunchFact = z.infer<typeof LaunchFactSchema>;

export const LaunchTierPriceSchema = z.object({
  minQuantity: z.number().int().positive(),
  maxQuantity: z.number().int().positive().nullable(),
  unitPrice: z.number().nonnegative(),
  currency: z.literal("CNY").default("CNY"),
  label: z.string().min(1).max(120),
}).strict();
export type LaunchTierPrice = z.infer<typeof LaunchTierPriceSchema>;

export const LaunchMinimumOrderQuantitySchema = z.object({
  quantity: z.number().int().positive(),
  unit: z.string().min(1).max(24),
  raw: z.string().min(1).max(120),
}).strict();
export type LaunchMinimumOrderQuantity = z.infer<typeof LaunchMinimumOrderQuantitySchema>;

/** SKU 分开保存颜色、款式、规格、价格和库存，不压成一个字符串。 */
export const LaunchSkuFactSchema = z.object({
  id: z.string().min(1).max(80),
  color: z.string().max(120).nullable(),
  style: z.string().max(120).nullable(),
  specification: z.string().max(160).nullable(),
  price: z.number().nonnegative().nullable(),
  stock: z.number().int().nonnegative().nullable(),
  source: LaunchFactSourceSchema,
}).strict();
export type LaunchSkuFact = z.infer<typeof LaunchSkuFactSchema>;

export const LaunchContaminationReasonSchema = z.enum([
  "navigation_term", "multiple_module_labels", "unparseable_quantity", "over_length", "missing_label_adjacency",
]);
export type LaunchContaminationReason = z.infer<typeof LaunchContaminationReasonSchema>;

/** 被丢弃的候选仍然留档：用户要能看到「系统读到了什么、为什么不用它」。 */
export const LaunchContaminationSchema = z.object({
  key: LaunchFactKeySchema,
  reason: LaunchContaminationReasonSchema,
  candidate: z.string().min(1).max(600),
  source: LaunchFactSourceSchema.optional(),
  detail: z.string().max(300).optional(),
}).strict();
export type LaunchContamination = z.infer<typeof LaunchContaminationSchema>;

export const LaunchMissingFactSchema = z.object({
  key: LaunchFactKeySchema,
  label: z.string().min(1).max(80),
  reason: z.string().min(1).max(240),
}).strict();
export type LaunchMissingFact = z.infer<typeof LaunchMissingFactSchema>;

export const LaunchSourcePageSchema = z.object({
  url: z.string().max(1200),
  title: z.string().max(500),
  domain: z.string().max(240),
}).strict();

export const LaunchFactPackSchema = z.object({
  /** 商品身份，用来在页面切换后判断「还是不是同一个商品」。 */
  productIdentity: z.string().min(1).max(200),
  sourcePage: LaunchSourcePageSchema,
  facts: z.array(LaunchFactSchema).max(40),
  skus: z.array(LaunchSkuFactSchema).max(40).default([]),
  contaminated: z.array(LaunchContaminationSchema).max(30).default([]),
  capturedAt: z.string(),
}).strict();
export type LaunchFactPack = z.infer<typeof LaunchFactPackSchema>;

/* ------------------------------- 标题清洗 ------------------------------- */

const SITE_SUFFIX_NAMES = [
  "阿里巴巴（中国）网络技术有限公司", "阿里巴巴(中国)网络技术有限公司", "阿里巴巴", "1688.com", "1688批发网", "1688",
  "alibaba.com", "alibaba", "淘宝网", "淘宝", "taobao.com", "taobao", "天猫", "tmall.com", "tmall",
  "京东", "jd.com", "拼多多", "yangkeduo.com", "抖音", "douyin.com", "义乌购",
];
const TITLE_SUFFIX_SEPARATOR = String.raw`\s*[-–—_|·]\s*`;
const DOMAIN_SUFFIX = String.raw`[a-z0-9][a-z0-9-]*(?:\.[a-z0-9-]+)*\.(?:com\.cn|com|cn|net|org|co\.uk)`;
const TITLE_SUFFIX_PATTERNS = [
  new RegExp(String.raw`^(.+?)${TITLE_SUFFIX_SEPARATOR}(?:${SITE_SUFFIX_NAMES.map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})$`, "i"),
  new RegExp(String.raw`^(.+?)${TITLE_SUFFIX_SEPARATOR}${DOMAIN_SUFFIX}$`, "i"),
];

/**
 * 商品标题不包含浏览器或站点标题后缀。
 * 只剥「分隔符 + 站点名／域名」，所以 "A-B 双头马克笔" 和 "折叠桌 – 1.2米" 原样返回。
 */
export function stripSiteTitleSuffix(title: string): string {
  let current = title.trim();
  for (let round = 0; round < 3; round += 1) {
    let stripped: string | undefined;
    for (const pattern of TITLE_SUFFIX_PATTERNS) {
      const match = pattern.exec(current);
      if (match?.[1]?.trim()) { stripped = match[1].trim(); break; }
    }
    if (stripped === undefined || stripped === current) break;
    current = stripped;
  }
  return current;
}

/* ------------------------------- 导航词判定 ------------------------------- */

export function navigationTermHits(value: string): string[] {
  return LAUNCH_NAVIGATION_TERMS.filter((term) => value.includes(term));
}

export function containsNavigationTerm(value: string): boolean {
  return navigationTermHits(value).length > 0;
}

/* ------------------------------- 数值解析 ------------------------------- */

const PRICE_MARKED = /(?:¥|￥)\s*(\d+(?:\.\d{1,2})?)|(\d+(?:\.\d{1,2})?)\s*元/;
const RANGE_QUANTITY = new RegExp(String.raw`(\d{1,7})\s*[-~～至]\s*(\d{1,7})\s*(${UNIT})`);
const OPEN_MIN_QUANTITY = new RegExp(String.raw`(?:≥|>=|不少于|至少|不低于)\s*(\d{1,7})\s*(${UNIT})`);
const MIN_SUFFIX_QUANTITY = new RegExp(String.raw`(\d{1,7})\s*(${UNIT})\s*(?:以上|起)`);

function markedPrice(segment: string): number | undefined {
  const match = PRICE_MARKED.exec(segment);
  const raw = match?.[1] ?? match?.[2];
  return raw === undefined ? undefined : Number(raw);
}

/**
 * 阶梯价解析。只接受带货币标记（¥／￥／元）的价格——
 * 「100-499件」这种没有货币标记的裸数字不是价格，否则会把数量当单价。
 */
export function parseTierPrices(text: string): LaunchTierPrice[] {
  const prices: LaunchTierPrice[] = [];
  for (const segment of text.split(/[；;、\n]/)) {
    const unitPrice = markedPrice(segment);
    if (unitPrice === undefined) continue;
    const range = RANGE_QUANTITY.exec(segment);
    if (range) {
      const minQuantity = Number(range[1]);
      const maxQuantity = Number(range[2]);
      if (maxQuantity < minQuantity) continue;
      prices.push({ minQuantity, maxQuantity, unitPrice, currency: "CNY", label: `${minQuantity}-${maxQuantity}${range[3]} ¥${unitPrice.toFixed(2)}` });
      continue;
    }
    const open = OPEN_MIN_QUANTITY.exec(segment) ?? MIN_SUFFIX_QUANTITY.exec(segment);
    if (open) {
      const minQuantity = Number(open[1]);
      prices.push({ minQuantity, maxQuantity: null, unitPrice, currency: "CNY", label: `≥${minQuantity}${open[2]} ¥${unitPrice.toFixed(2)}` });
    }
  }
  return prices.sort((left, right) => left.minQuantity - right.minQuantity);
}

const MOQ_LEAD = /^(?:≥|>=|不少于|至少|不低于|起订量|起批量|起批|起订)\s*[:：]?\s*/;
const MOQ_TAIL = /\s*(?:起批|起订|以上|起)?\s*$/;
const MOQ_CORE = new RegExp(String.raw`^(\d{1,7})\s*(${UNIT})$`, "i");

/**
 * 起批量只接受可解析的数量与单位。
 * "100-499件" 是阶梯区间不是起批量，"面议""大量""100" 都不可解析。
 */
export function parseMinimumOrderQuantity(value: string): LaunchMinimumOrderQuantity | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const core = trimmed.replace(MOQ_LEAD, "").replace(MOQ_TAIL, "").trim();
  const match = MOQ_CORE.exec(core);
  if (!match) return null;
  return { quantity: Number(match[1]), unit: match[2]!, raw: trimmed };
}

/* ------------------------------- 污染检测 ------------------------------- */

/**
 * 抽取值出现下列特征时判为不可信，不进入 Listing：
 * 导航／模块名、多个模块标签、过长的材质或规格、不可解析的数量、缺少标签邻接关系。
 *
 * 邻接关系只对 `page_observed` 生效：截图识别本来就没有 DOM 邻接关系，
 * 它是「还没被用户确认」，不是「被污染」。
 */
export function detectLaunchFactContamination(fact: LaunchFact): LaunchContaminationReason[] {
  const reasons: LaunchContaminationReason[] = [];
  const hits = navigationTermHits(fact.value);
  if (hits.length > 0) reasons.push("navigation_term");
  if (hits.length > 1) reasons.push("multiple_module_labels");
  const limit = LAUNCH_FACT_LENGTH_LIMITS[fact.key];
  if (limit !== undefined && fact.value.trim().length > limit) reasons.push("over_length");
  if (fact.key === "minimum_order_quantity" && !parseMinimumOrderQuantity(fact.value)) reasons.push("unparseable_quantity");
  if (fact.key === "tier_prices" && parseTierPrices(fact.value).length === 0) reasons.push("unparseable_quantity");
  if (fact.source === "page_observed" && !fact.labelAdjacency) reasons.push("missing_label_adjacency");
  return reasons;
}

export function isTrustedLaunchFact(fact: LaunchFact): boolean {
  return isListingTrustedLaunchFactSource(fact.source) && detectLaunchFactContamination(fact).length === 0;
}

/* ------------------------------- 派生视图 ------------------------------- */

const normalizeFactValue = (value: string) => value.trim().replace(/\s+/g, "").toLowerCase();

export function trustedLaunchFacts(pack: LaunchFactPack): LaunchFact[] {
  return pack.facts.filter(isTrustedLaunchFact);
}

function candidatesFor(pack: LaunchFactPack, key: LaunchFactKey): LaunchFact[] {
  return trustedLaunchFacts(pack).filter((fact) => fact.key === key);
}

/**
 * 同一个 key 的候选池：有用户确认就以用户确认为准（那是用户做的裁决），
 * 否则全部观察候选一起比较。多个不同值 = 冲突，调用方必须要求用户裁决。
 */
function adjudicatedCandidates(pack: LaunchFactPack, key: LaunchFactKey): LaunchFact[] {
  const candidates = candidatesFor(pack, key);
  const confirmed = candidates.filter((fact) => fact.source === "user_confirmed");
  return confirmed.length > 0 ? confirmed : candidates;
}

/** 只有一个可信值（或用户已裁决）时才返回；冲突、缺失、污染都返回 undefined。 */
export function resolvedLaunchFact(pack: LaunchFactPack, key: LaunchFactKey): LaunchFact | undefined {
  const candidates = adjudicatedCandidates(pack, key);
  if (candidates.length === 0) return undefined;
  const distinct = new Set(candidates.map((fact) => normalizeFactValue(fact.value)));
  return distinct.size === 1 ? candidates[0] : undefined;
}

/** 页面／截图／用户三种来源互相矛盾、且用户还没有裁决的 key。 */
export function launchFactConflicts(pack: LaunchFactPack): Array<{ key: LaunchFactKey; values: string[] }> {
  const keys = [...new Set(pack.facts.map((fact) => fact.key))];
  return keys.flatMap((key) => {
    const candidates = adjudicatedCandidates(pack, key);
    if (candidates.length === 0) return [];
    const values = [...new Set(candidates.map((fact) => fact.value))];
    return values.length > 1 ? [{ key, values }] : [];
  });
}

export function resolvedTierPrices(pack: LaunchFactPack): LaunchTierPrice[] {
  const fact = resolvedLaunchFact(pack, "tier_prices");
  return fact ? parseTierPrices(fact.value) : [];
}

export function resolvedMinimumOrderQuantity(pack: LaunchFactPack): LaunchMinimumOrderQuantity | null {
  const fact = resolvedLaunchFact(pack, "minimum_order_quantity");
  return fact ? parseMinimumOrderQuantity(fact.value) : null;
}

/**
 * Listing 文案可以消费的事实：可信、非成本、且高风险项已获用户确认。
 * 未知项不在这里——它们进待核实列表，不写成商品卖点。
 */
export function listingCopyFacts(pack: LaunchFactPack): LaunchFact[] {
  const keys = [...new Set(pack.facts.map((fact) => fact.key))];
  return keys.flatMap((key) => {
    if (LAUNCH_FACT_COPY_EXCLUDED_KEYS.includes(key)) return [];
    const fact = resolvedLaunchFact(pack, key);
    if (!fact) return [];
    if (LAUNCH_FACT_HIGH_RISK_KEYS.includes(key) && fact.source !== "user_confirmed") return [];
    return [fact];
  });
}

/**
 * 待核实原因：**从来没读到过**这一项。
 *
 * 与"已读到值、只等用户确认"分开是有后果的：前者截图兜底还有机会补上，
 * 后者（材质/品牌/认证/合规这些高风险项）再拍多少张都不会变，
 * 只能由用户在事实包里裁决。服务端据此决定还要不要请客户端再拍一轮。
 */
export const MISSING_LAUNCH_FACT_REASON = "尚未从页面或截图核实，需要用户确认";

/**
 * 待核实列表。**由事实派生**，不读事实包里可能过期的缓存副本，
 * 因此「页面上显示了待核实项」与「门禁按同一批项拦截」永远一致。
 */
export function deriveMissingLaunchFacts(pack: LaunchFactPack): LaunchMissingFact[] {
  return LAUNCH_FACT_VERIFY_KEYS.flatMap((key) => {
    const fact = resolvedLaunchFact(pack, key);
    if (!fact) return [{ key, label: LAUNCH_FACT_LABELS[key], reason: MISSING_LAUNCH_FACT_REASON }];
    if (LAUNCH_FACT_HIGH_RISK_KEYS.includes(key) && fact.source !== "user_confirmed") {
      return [{ key, label: LAUNCH_FACT_LABELS[key], reason: "已读取到值，但写进 Listing 前需要用户确认" }];
    }
    return [];
  });
}

export interface LaunchFactPackReadiness {
  ready: boolean;
  conflicts: LaunchFactKey[];
  missingRequired: LaunchFactKey[];
  pendingReview: LaunchMissingFact[];
}

/** 生成 Listing 前的门禁：必需项可信 + 没有未裁决冲突。 */
export function launchFactPackReadiness(pack: LaunchFactPack): LaunchFactPackReadiness {
  const conflicts = launchFactConflicts(pack).map((conflict) => conflict.key);
  const missingRequired = LAUNCH_FACT_REQUIRED_FOR_LISTING.filter((key) => !resolvedLaunchFact(pack, key));
  return {
    ready: conflicts.length === 0 && missingRequired.length === 0,
    conflicts,
    missingRequired,
    pendingReview: deriveMissingLaunchFacts(pack),
  };
}

/* ------------------------------- 整页截图兜底 ------------------------------- */

/**
 * 截图识别出来的候选值。识别结果是模型给的**未知 JSON**，形状校验（别名、长度、
 * 类型）由 API 侧的 `launch-facts-screenshot.ts` 负责；这里只接收已经规范好的
 * 每 key 一个值，保证「值从哪来、算什么来源」这套判定只有一份。
 */
export const SCREENSHOT_REGION_FIELD = "field" as const;
export const SCREENSHOT_REGION_WHOLE_PAGE = "whole_page" as const;
export type ScreenshotRegionLabel = "whole_page" | "field";

/** 截图识别结果里每个值本身的长度上限；超过即判为吞进了相邻内容，按未确认处理。 */
export const SCREENSHOT_FACT_VALUE_MAX_LENGTH = 600;
/** 一次截图最多能带进来的字段数——超过就不是「识别」，是把整页 OCR 倒进来。 */
export const SCREENSHOT_PAYLOAD_MAX_KEYS = 32;

export interface LaunchScreenshotObservation {
  /** 截图所属商品身份。与事实包不一致 = 页面切换竞态，整个观察必须被拒绝。 */
  productIdentity: string;
  capturedAt: string;
  /** 只做整页可见区域截图；`field` 留给将来缩小区域时区分。 */
  region: ScreenshotRegionLabel;
  /** 识别到的值（已按 key 规范化）。 */
  values: Partial<Record<LaunchFactKey, string>>;
  /** 截图里没读到、或读出来不可用的 key：它们只能停在 `needs_review`。 */
  unconfirmedKeys: LaunchFactKey[];
  /** 已脱敏的页面地址（查询串与锚点必须剥掉）。 */
  pageUrl: string;
}

export interface ScreenshotObservationMerge {
  pack: LaunchFactPack;
  /** 合并后仍未裁决的冲突 key。调用方必须把冲突交给用户，不得替用户选边。 */
  conflicts: LaunchFactKey[];
  /** 该观察被整体拒绝的原因（商品身份不符、观察为空等）。 */
  refusal?: string;
}

const SCREENSHOT_UNCONFIRMED_REASON = "整页截图未能确认这一项，需要用户核对";
const SCREENSHOT_MISMATCH_REFUSAL = "截图所属商品身份与当前事实包不一致，已拒绝合并（页面可能已经切换）";

/**
 * 识别不到值、值吞了相邻内容、或没有可追踪的页面地址时，一律按未确认处理，
 * 不把截图结果写成可信事实。用户在确认环节看到的必须是「没读到」，不是猜出来的值。
 */
function observedScreenshotFact(observation: LaunchScreenshotObservation, key: LaunchFactKey): LaunchFact | undefined {
  const raw = observation.values[key]?.trim();
  if (!raw || raw.length > SCREENSHOT_FACT_VALUE_MAX_LENGTH) return undefined;
  return LaunchFactSchema.parse({
    key,
    label: LAUNCH_FACT_LABELS[key],
    value: raw,
    raw,
    source: "screenshot_observed",
    observedAt: observation.capturedAt,
    pageUrl: observation.pageUrl,
    region: observation.region,
    // 截图没有 DOM 邻接关系可证明，也没有污染一说，所以只标邻接为 false。
    labelAdjacency: false,
    warnings: [],
  });
}

function unconfirmedScreenshotFact(observation: LaunchScreenshotObservation, key: LaunchFactKey): LaunchFact {
  return LaunchFactSchema.parse({
    key,
    label: LAUNCH_FACT_LABELS[key],
    value: SCREENSHOT_UNCONFIRMED_REASON,
    source: "needs_review",
    observedAt: observation.capturedAt,
    pageUrl: observation.pageUrl,
    region: observation.region,
    labelAdjacency: false,
    warnings: [SCREENSHOT_UNCONFIRMED_REASON],
  });
}

const normalizedFactValue = normalizeFactValue;

/**
 * 把一次截图观察合进事实包。这是**追加**语义，不是覆盖：
 *
 * - 已有事实永不删除、永不降级；用户确认过的 key 直接跳过，截图不推翻用户裁决；
 * - 识别到的值新增一条 `screenshot_observed`；同一 key 已有相同值不重复堆叠；
 * - 没读到的 key 记 `needs_review`，让待核实列表继续拦住它；
 * - 页面／截图给出不同值时两条观察并存，由 `launchFactConflicts` 暴露冲突，
 *   用户确认之前 `resolvedLaunchFact` 返回 undefined。
 */
export function applyScreenshotObservation(pack: LaunchFactPack, observation: LaunchScreenshotObservation): ScreenshotObservationMerge {
  if (observation.productIdentity !== pack.productIdentity) {
    return { pack, conflicts: [], refusal: SCREENSHOT_MISMATCH_REFUSAL };
  }
  const hasReadableValue = LaunchFactKeySchema.options.some((key) => Boolean(observation.values[key]?.trim()));
  if (!hasReadableValue && observation.unconfirmedKeys.length === 0) {
    return { pack, conflicts: [], refusal: "截图识别没有给出任何字段，未写入事实包" };
  }

  // 用户已经裁决过的 key 不再追加观察，否则会把「用户确认」重新变成「多源冲突」。
  const confirmedKeys = new Set(pack.facts.filter((fact) => fact.source === "user_confirmed").map((fact) => fact.key));
  const candidates: LaunchFact[] = [];
  const unusable: LaunchFactKey[] = [];
  for (const key of LaunchFactKeySchema.options) {
    if (confirmedKeys.has(key)) continue;
    const fact = observedScreenshotFact(observation, key);
    if (fact) { candidates.push(fact); continue; }
    // 没这个 key 是「截图没读到」；有值但为空或过长是「读到的东西不能用」。
    // 两种都必须留在未确认里——如果只对前者记 needs_review，过长的脏值会变成「没读到也没问题」。
    const declared = observation.values[key];
    if (declared !== undefined || observation.unconfirmedKeys.includes(key)) unusable.push(key);
  }
  // 未确认的 key 去重后追加；已有同 key 的未确认记录不重复写。
  const recordedUnconfirmed = new Set(pack.facts.filter((fact) => fact.source === "needs_review").map((fact) => fact.key));
  const unconfirmed: LaunchFact[] = [];
  for (const key of [...new Set([...observation.unconfirmedKeys, ...unusable])]) {
    if (confirmedKeys.has(key) || recordedUnconfirmed.has(key)) continue;
    if (candidates.some((fact) => fact.key === key)) continue;
    recordedUnconfirmed.add(key);
    unconfirmed.push(unconfirmedScreenshotFact(observation, key));
  }

  // 同一来源、同一 key、同一值只留一条：截几次屏不该把事实堆成重复列表。
  const fingerprint = (fact: LaunchFact) => `${fact.key}:${fact.source}:${normalizedFactValue(fact.value)}`;
  const existing = new Set(pack.facts.filter((fact) => fact.source === "screenshot_observed").map(fingerprint));
  const facts = [...pack.facts, ...[...candidates, ...unconfirmed].filter((fact) => fact.source !== "screenshot_observed" || !existing.has(fingerprint(fact)))];
  const merged = LaunchFactPackSchema.parse({ ...pack, facts });
  const conflicts = launchFactConflicts(merged)
    .map((conflict) => conflict.key)
    .filter((key) => candidates.some((fact) => fact.key === key));
  return { pack: merged, conflicts };
}
